/**
 * SectionCompleteButton — Small checkmark toggle in section headers
 * Placed in the top-right corner of each tab section
 * Grey outline → green filled on click
 * Sprint 8 — S8-24
 */

import { useState } from 'react'

export interface SectionCompleteButtonProps {
  isComplete: boolean
  completedByName?: string | null
  completedAt?: string | null
  onToggle: () => void
  disabled?: boolean
}

export function SectionCompleteButton({
  isComplete,
  completedByName,
  completedAt,
  onToggle,
  disabled = false,
}: SectionCompleteButtonProps) {
  const [isHovered, setIsHovered] = useState(false)

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return ''
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays === 0) return 'today'
    if (diffDays === 1) return 'yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  }

  const tooltip = isComplete
    ? `Completed${completedByName ? ` by ${completedByName}` : ''}${completedAt ? `, ${formatDate(completedAt)}` : ''}`
    : 'Mark section complete'

  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled) onToggle()
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      title={tooltip}
      className="
        relative flex-shrink-0
        w-[22px] h-[22px]
        rounded-full
        flex items-center justify-center
        transition-all duration-150
        focus:outline-none
      "
      style={{
        backgroundColor: isComplete ? '#22C55E' : (isHovered ? 'rgba(34, 197, 94, 0.15)' : 'transparent'),
        border: isComplete ? '2px solid #22C55E' : `2px solid ${isHovered ? '#22C55E' : '#3a3a5a'}`,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {/* Checkmark icon */}
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        stroke={isComplete ? '#ffffff' : (isHovered ? '#22C55E' : '#5a5a6c')}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2.5 6L5 8.5L9.5 3.5" />
      </svg>
    </button>
  )
}

export default SectionCompleteButton
